import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n';
import {
  fetchWinBackPromotion,
  fetchWinBackRewards,
  setWinBackEnabled,
} from '../api/promotions';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function formatMoney(amount, currency) {
  const value = Number(amount);
  const formatted = Number.isFinite(value) ? value.toFixed(2) : String(amount ?? '—');
  return `${formatted} ${String(currency || '').toUpperCase()}`.trim();
}

function customerName(reward) {
  const name = [reward.first_name, reward.last_name].filter(Boolean).join(' ').trim();
  return name || reward.customer_phone || reward.binding_phone || `#${reward.customer_id}`;
}

export default function WinBackPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const token = user?.api_token;
  const [promotion, setPromotion] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadData = useCallback(async (page = pagination.page, limit = pagination.limit) => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const [promotionData, rewardsData] = await Promise.all([
        fetchWinBackPromotion(token),
        fetchWinBackRewards(token, page, limit),
      ]);
      setPromotion(promotionData);
      setRewards(Array.isArray(rewardsData?.items) ? rewardsData.items : []);
      setPagination(rewardsData?.pagination || { page, limit, total: 0 });
    } catch (requestError) {
      setError(requestError.message || t('Ошибка загрузки акции'));
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.page, t, token]);

  useEffect(() => {
    loadData(1, pagination.limit);
    // Initial load is intentionally tied only to the authenticated token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!user || user.role !== 'admin') {
    return <Alert severity="error">{t('Доступ запрещён. Только для администратора.')}</Alert>;
  }

  const toggleEnabled = async (enabled) => {
    const previous = promotion;
    setPromotion((current) => ({ ...current, enabled }));
    setSaving(true);
    try {
      setPromotion(await setWinBackEnabled(token, enabled));
      setSnackbar({
        open: true,
        message: t(enabled ? 'Акция включена' : 'Акция выключена'),
        severity: 'success',
      });
    } catch (requestError) {
      setPromotion(previous);
      setSnackbar({
        open: true,
        message: requestError.message || t('Ошибка обновления статуса акции'),
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const changePage = (_, zeroBasedPage) => {
    loadData(zeroBasedPage + 1, pagination.limit);
  };

  const changeRowsPerPage = (event) => {
    loadData(1, Number(event.target.value));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{t('Win-back')}</Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={t('Обновить')}>
          <IconButton onClick={() => loadData()} disabled={loading}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 280 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('Возвращение клиентов')}</Typography>
            <Typography color="text.secondary">
              {t('50% от суммы платежа на баланс после 90 дней без успешных заказов')}
            </Typography>
          </Box>
          <FormControlLabel
            control={(
              <Switch
                checked={Boolean(promotion?.enabled)}
                onChange={(event) => toggleEnabled(event.target.checked)}
                disabled={!promotion || saving}
              />
            )}
            label={promotion?.enabled ? t('Акция включена') : t('Акция выключена')}
          />
        </Box>
      </Paper>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>{t('История начислений')}</Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('Клиент')}</TableCell>
              <TableCell>{t('Заказ')}</TableCell>
              <TableCell>{t('Предыдущий успешный заказ')}</TableCell>
              <TableCell align="right">{t('Сумма платежа')}</TableCell>
              <TableCell align="right">{t('Бонус')}</TableCell>
              <TableCell>{t('Дата начисления')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}><CircularProgress /></TableCell>
              </TableRow>
            ) : rewards.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  {t('Начислений пока нет')}
                </TableCell>
              </TableRow>
            ) : rewards.map((reward) => (
              <TableRow key={reward.id} hover>
                <TableCell>
                  <Typography sx={{ fontWeight: 600 }}>{customerName(reward)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {reward.customer_email || `ID: ${reward.customer_id}`}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography>{reward.external_order_id || `#${reward.order_id}`}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {reward.binding_phone || reward.customer_phone || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography>{reward.previous_external_order_id || `#${reward.previous_order_id}`}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(reward.previous_payment_at)}
                  </Typography>
                </TableCell>
                <TableCell align="right">{formatMoney(reward.base_amount, reward.currency)}</TableCell>
                <TableCell align="right">
                  <Typography sx={{ fontWeight: 700, color: 'success.main' }}>
                    +{formatMoney(reward.reward_amount, reward.currency)}
                  </Typography>
                </TableCell>
                <TableCell>{formatDate(reward.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={pagination.total || 0}
          page={Math.max(0, (pagination.page || 1) - 1)}
          rowsPerPage={pagination.limit || 25}
          onPageChange={changePage}
          onRowsPerPageChange={changeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={t('Строк на странице')}
        />
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
